import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { Warning } from '../../components/LessonUI';

const PythonStringFormat = () => {
    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">String Upgrade</div>
                <h1>F-Strings & Optimized I/O</h1>
                <p className="hero-lead">
                    Injecting variables into strings used to be messy.
                    Then came Python 3.6+ and the <strong>F-String</strong>: a feature so good it changed how we write Python forever.
                </p>
            </div>

            <div className="content-section chapter-intro">
                <p>
                    String formatting is the act of injecting data (variables, database usage, user names) into a static text template.
                    Python has tried to solve this problem three times.
                </p>
                <div className="timeline-box">
                    <ul>
                        <li><span className="year">1991</span> <strong>% Operator:</strong> The C-style legacy. Error-prone and limited.</li>
                        <li><span className="year">2008</span> <strong>.format():</strong> The "New" Style. Powerful but verbose.</li>
                        <li><span className="year">2016</span> <strong>F-Strings:</strong> (Formatted String Literals). Fast, readable, and expressions supported inside <code>{"{ }"}</code>.</li>
                    </ul>
                </div>

                <div className="learning-objectives">
                    <h3>What You'll Learn</h3>
                    <ul>
                        <li><strong>Modern Syntax:</strong> Using <code>f"Hello &#123;name&#125;"</code>.</li>
                        <li><strong>Advanced Specifiers:</strong> Controlling decimals, padding, and alignment with <code>:</code>.</li>
                        <li><strong>Debugging Magic:</strong> The hidden power of <code>f"&#123;var=&#125;"</code>.</li>
                        <li><strong>Performance:</strong> Why f-strings are faster than <code>.format()</code>.</li>
                        <li><strong>Legacy Patterns:</strong> Recognizing old code using <code>%s</code>.</li>
                    </ul>
                </div>
            </div>

            <div className="content-section">
                <h2>The Winner: F-Strings</h2>
                <p>
                    F-Strings (Formatted String Literals) are string literals prefixed with <code>f</code> or <code>F</code>.
                    They allow you to execute Python expressions directly inside curly braces.
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>Basic Injection</span>
                    </div>
                    <CodeBlock language="python" code={`name = "Alice"
age = 30
balance = 1250.50

# The F-String Way
# Readable. Evaluation happens at runtime.
message = f"User {name} is {age} years old."
print(message)

# Expressions inside braces!
# You can do math, function calls, or method access
print(f"Next year, {name} will be {age + 1}.")
print(f"Name uppercase: {name.upper()}")`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Format Specifiers: Perfectionism</h2>
                <p>
                    Just injecting values isn't enough. You often need to control decimal places, add leading zeros, or align text.
                    You do this by adding a colon <code>:</code> followed by a <strong>Format Specifier</strong>.
                </p>

                <h3>Common Specifiers Cheat Sheet</h3>
                <div className="table-container">
                    <table className="course-table">
                        <thead>
                            <tr>
                                <th style={{ width: '20%' }}>Specifier</th>
                                <th style={{ width: '40%' }}>Example</th>
                                <th style={{ width: '40%' }}>Description</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td><code>:.2f</code></td>
                                <td><code>f"&#123;pi:.2f&#125;"</code>  <code>3.14</code></td>
                                <td>Float precision (2 decimal places)</td>
                            </tr>
                            <tr>
                                <td><code>:03d</code></td>
                                <td><code>f"&#123;id:03d&#125;"</code>  <code>005</code></td>
                                <td>Pad with zeros to length 3</td>
                            </tr>
                            <tr>
                                <td><code>:,</code></td>
                                <td><code>f"&#123;val:,&#125;"</code>  <code>1,000,000</code></td>
                                <td>Thousands separator</td>
                            </tr>
                            <tr>
                                <td><code>:^10</code></td>
                                <td><code>f"&#123;x:^10&#125;"</code>  <code>"  Hi  "</code></td>
                                <td>Center align (padding 10)</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div className="code-example">
                    <div className="code-header">
                        <span>Financial Formatting Example</span>
                    </div>
                    <CodeBlock language="python" code={`price = 49.9
tax = 0.08
total = price * (1 + tax)

# Without formatting
print(f"Total: {total}") 
# Output: Total: 53.892000000000001 (Ugly float math)

# With Format Specifiers
# .2f = 2 decimal places fixed formatting
print(f"Total: \${total:.2f}") 
# Output: Total: $53.89`} />
                </div>
            </div>

            <div className="content-section">
                <h2>The Debugging Superpower: <code>=</code></h2>
                <p>
                    Introduced in Python 3.8, this is the single most useful trick for debugging without a debugger.
                    If you add <code>=</code> inside the braces, Python prints the <strong>variable name</strong> AND its <strong>value</strong>.
                </p>

                <CodeBlock language="python" code={`user = "admin"
retries = 3

#  Old Debugging
print(f"user: {user}, retries: {retries}")

# The "Walrus-style" Debugging (Self-documenting)
print(f"{user=}, {retries=}")
# Output: "user='admin', retries=3"`} />
            </div>

            <div className="content-section">
                <h2>Performance Shootout</h2>
                <p>
                    Why are F-Strings faster?
                    Legacy <code>%</code> formatting and <code>.format()</code> are parsed at runtime.
                    F-Strings are treated by the compiler as a series of constant strings and operations, optimizing them directly into efficient bytecode.
                </p>

                <div className="code-example">
                    <CodeBlock language="python" code={`import timeit

name = "Python"
age = 30

# 1. Percent Styling (Slowest legacy)
# "Hello %s" % name

# 2. .format() (Slow object method)
# "Hello {}".format(name)

# 3. f-string (Fast opcode)
# f"Hello {name}"

# Benchmark results usually show f-strings are ~2x faster than .format()!`} />
                </div>
            </div>

            <Warning><h2>When NOT to use F-Strings?</h2>
                <p>
                    F-Strings are evaluated <strong>eagerly</strong> (immediately). This makes them unsuitable for:
                </p>
                <ul>
                    <li><strong>Logging:</strong> <code>logging.info(f"User &#123;user&#125;")</code> is bad because it interpolates even if logging is disabled. Use <code>logging.info("User %s", user)</code> instead.</li>
                    <li><strong>i18n (Internationalization):</strong> Translation tools generally need a static string key like <code>"Hello &#123;name&#125;"</code> to look up translations. F-Strings bake the value in too early.</li>
                </ul></Warning>

        </div>
    );
};

export default PythonStringFormat;


